import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import EndUserPlannerRecommenderStepDays from './end_user_planner_recommender_step_days';
// import EndUserPlannerRecommenderStepStartDate from './end_user_planner_recommender_step_start_date';
import { trackEvents } from '../../operations/tracking_operations';
import {
  numDaysInProgramme,
  programmeDaysForCalendar,
} from '../../services/programmes';
import {
  useCreateContentEntryMutation,
  useCreateMealMutation,
  useGetSharedContentEntriesByParentQuery,
  useGetSharedContentEntriesByParentsQuery,
  useGetSharedProgrammeQuery,
  useLazyGetFullMealsByParentsQuery,
  useLazyGetFullMealQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { userLocale } from '../../services/users';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { programmeStartCalendarDay } from '../../services/my_day';
import {
  buildExistingDaysFromProgrammePrescribedDays,
  buildExistingMealsFromProgrammePrescribedMeals,
  RECOMMENDER_ENTRY_TYPE_CONTENT_ENTRY,
  RECOMMENDER_ENTRY_TYPE_MEAL,
} from '../../services/recommender';
import {
  mealWithIngredientTokensUpdated,
  mealWithScaledIngredients,
} from '../../services/meals';
import { cloneObject } from '../../operations/utils';
import { PlannerEntryType, RecommendationSource } from '../../API';
import {
  calorieSplitsAreValid,
  DEFAULT_PROGRAMME_CALORIE_SPLITS,
  DEFAULT_PROGRAMME_MEAL_TYPES,
} from '../../services/meal_types';
import { recommendMealsOperation } from '../../operations/recommender_operations';
import { recommenderPersonalisationDataFromOnboardingAnswers } from '../../services/space_onboarding';
import {
  dbWeekStartDatesAndDayIndexesCoveringRecommendationRequest,
  plannerWeekWithContentEntryAdded,
  plannerWeekWithMealAdded,
} from '../../services/planner';
import { deduplicate, flattenObjects } from '../../services/arrays';
import { currentProgrammeTargetCalories } from '../../services/end_user_space_memberships';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  analyzeMissingIngredientsOperation,
  scaleMeal,
} from '../../operations/meal_operations';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetSharedContentBoardsForEndUserQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { getOrCreatePlannerWeeks } from '../../operations/planner_operations';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

// const STEP_START_DATE = 'start_date';
const STEP_DAYS = 'days';

const servingsFor = (numAdults, numChildren) => numAdults + numChildren * 0.5;

const EndUserPlannerRecommenderWizard = () => {
  const {
    startDate: routePlannerViewWeekStartDate,
    dayIndex: routeStartDayIndex,
  } = useParams();

  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment, isLoading: enrollmentLoading } =
    useCurrentProgrammeEnrollmentQuery();

  // console.log(JSON.stringify({ currentProgrammeEnrollment }));

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const { data: sharedProgramme, isLoading: isSharedProgrammeLoading } =
    useGetSharedProgrammeQuery(sharedProgrammeID, {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    });

  const { data: ownedRecipesBoards, isLoading: isOwnedRecipesBoardsLoading } =
    useGetOwnedRecipesBoardsQuery();

  const ownedRecipesBoardIDs = (ownedRecipesBoards || []).map(
    (board) => board.id,
  );

  const sharedProgrammeAssociatedRecipesBoardIDs = useMemo(
    () => sharedProgramme?.recipesBoardIDs || [],
    [sharedProgramme?.recipesBoardIDs],
  );

  const {
    data: sharedContentEntriesOwnedBySharedProgramme,
    isLoading: isSharedContentEntriesOwnedBySharedProgrammeLoading,
  } = useGetSharedContentEntriesByParentQuery(sharedProgramme?.id, {
    skip: !sharedProgramme?.id,
  });

  const { data: sharedContentBoards, isLoading: sharedContentBoardsLoading } =
    useGetSharedContentBoardsForEndUserQuery();

  const sharedContentBoardIDs = useMemo(
    () => (sharedContentBoards || []).map((board) => board.id),
    [sharedContentBoards],
  );

  const {
    data: sharedContentEntriesByParents,
    isLoading: isSharedContentEntriesByParentsLoading,
  } = useGetSharedContentEntriesByParentsQuery(sharedContentBoardIDs, {
    skip: sharedContentBoardIDs.length === 0,
  });

  const sharedContentEntriesPublishedToSpace = useMemo(
    () => flattenObjects(Object.values(sharedContentEntriesByParents || {})),
    [sharedContentEntriesByParents],
  );

  const sharedContentEntries = useMemo(
    () => ({
      ...sharedContentEntriesOwnedBySharedProgramme,
      ...sharedContentEntriesPublishedToSpace,
    }),
    [
      sharedContentEntriesOwnedBySharedProgramme,
      sharedContentEntriesPublishedToSpace,
    ],
  );

  const anyResourceLoading =
    isUserProfileLoading ||
    spaceMembershipLoading ||
    enrollmentLoading ||
    isSharedProgrammeLoading ||
    isOwnedRecipesBoardsLoading ||
    isSharedContentEntriesOwnedBySharedProgrammeLoading ||
    sharedContentBoardsLoading ||
    isSharedContentEntriesByParentsLoading;

  const programmeStartDay = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );

  const initialViewWeekStartDate = routePlannerViewWeekStartDate; /* ||
    currentViewWeekStartDateStr(plannerViewWeekStartDay) */

  const initialStartDayIndex = /* routePlannerViewWeekStartDate
    ? routeStartDayIndex
    : dayIndexForToday(); */ routeStartDayIndex;

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const steps = useMemo(() => {
    const tempSteps = [];
    // if (!routePlannerViewWeekStartDate) {
    //   tempSteps.push(STEP_START_DATE);
    // }
    tempSteps.push(STEP_DAYS);
    return tempSteps;
  }, []);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];
  const [isRecommenderRunning, setIsRecommenderRunning] = useState(false);
  // const [plannerViewWeekStartDate, setPlannerViewWeekStartDate] = useState(
  //   initialViewWeekStartDate,
  // );
  const plannerViewWeekStartDate = initialViewWeekStartDate;
  // const [startDayIndex, setStartDayIndex] = useState(initialStartDayIndex);
  const startDayIndex = initialStartDayIndex;

  const copyMealsExactly = sharedProgramme?.copyMealsExactly;

  const totalDays = sharedProgramme && numDaysInProgramme(sharedProgramme);

  const [numDaysToPlan, setNumDaysToPlan] = useState(
    copyMealsExactly ? totalDays : 7,
  );
  const [servingsAdults, setServingsAdults] = useState(2);
  const [servingsChildren, setServingsChildren] = useState(0);
  const [recommendMealsError, setRecommendMealsError] = useState(false);

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetFullMealsByParents] = useLazyGetFullMealsByParentsQuery();

  const locales = useMemo(
    () => sharedProgramme?.locales || [userLocale(userProfile)],
    [sharedProgramme?.locales, userProfile],
  );

  const navigateToPlanner = useCallback(() => {
    if (plannerViewWeekStartDate && startDayIndex) {
      navigate(`/planner/${plannerViewWeekStartDate}#${startDayIndex}`);
      return;
    }
    if (plannerViewWeekStartDate) {
      navigate(`/planner/${plannerViewWeekStartDate}`);
      return;
    }
    navigate('/planner');
  }, [navigate, plannerViewWeekStartDate, startDayIndex]);

  const navigateBack = useCallback(() => {
    if (plannerViewWeekStartDate && startDayIndex) {
      navigate(`/planner/${plannerViewWeekStartDate}#${startDayIndex}`);
      return;
    }
    if (plannerViewWeekStartDate) {
      navigate(`/planner/${plannerViewWeekStartDate}`);
      return;
    }
    navigate(-1);
  }, [navigate, plannerViewWeekStartDate, startDayIndex]);

  const dbWeeksAndIndexes =
    dbWeekStartDatesAndDayIndexesCoveringRecommendationRequest(
      plannerViewWeekStartDate,
      startDayIndex,
      numDaysToPlan,
    );

  const affectedDbWeekStartDates = deduplicate(
    dbWeeksAndIndexes.map((dbwi) => dbwi.dbWeekStartDate),
  );

  console.log({ affectedDbWeekStartDates });

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [createContentEntry] = useCreateContentEntryMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  const callRecommender = useCallback(async () => {
    const dbPlannerWeeksByStartDate = await getOrCreatePlannerWeeks(
      affectedDbWeekStartDates,
      lazyGetOrCreateDbPlannerWeekByStartDate,
    );

    const numServings = servingsFor(servingsAdults, servingsChildren);

    const currentHealthProGroup = null;

    console.log({
      currentSpaceMembershipID: currentSpaceMembership?.id,
      plannerViewWeekStartDate,
      startDayIndex,
    });

    if (!sharedProgramme) {
      console.warn(
        `Could not find shared programme with ID ${sharedProgrammeID}`,
      );
      return;
    }
    const { personalisedMealScaling } = sharedProgramme;
    // const sharedProgrammeEmbeddedRecipesBoardID =
    //   sharedProgramme.recipesBoard?.id;

    // if (sharedProgrammeEmbeddedRecipesBoardID) {
    //   recipesBoardsIDs.push(sharedProgrammeEmbeddedRecipesBoardID);
    // }

    // const databaseRecipesBoardID = sharedProgramme.databaseRecipesBoard?.id;
    // if (databaseRecipesBoardID) {
    //   recipesBoardsIDs.push(databaseRecipesBoardID);
    // }

    const programmeDays = programmeDaysForCalendar(
      sharedProgramme.plans,
      programmeStartDay,
      dbWeeksAndIndexes[0].dbWeekStartDate,
      dbWeeksAndIndexes[0].dayIndex,
      numDaysToPlan,
    );

    console.log(JSON.stringify(programmeDays));

    // const existingDays = dbWeeksAndIndexes.map((dbwi, indexZeroBased) => {
    //   const plannerWeekDay =
    //     getState().mealboard.plannerWeeks[dbwi.dbWeekStartDate].days[
    //       daysArrayIndex(dbwi.dayIndex)
    //     ];
    //   return {
    //     dayIndex: indexZeroBased + 1,
    //     entries: plannerWeekDay.entries
    //       .filter((entry) => ['MEAL'].includes(entry.programmeEntryType))
    //       .filter(
    //         (entry) =>
    //           // Only entries which are linked to valid meals/GRC recipes
    //           entry.programmeEntryType === 'MEAL' &&
    //           !!(meals[entry.objectID]?.recipes || [])[0],
    //       )
    //       .map((entry) => ({
    //         entryType: RECOMMENDER_ENTRY_TYPE_MEAL,
    //         mealType:
    //           entry.programmeEntryType ===
    //           ((meals[entry.objectID]?.recipes || [])[0].mealTypes || [])[0],
    //         meals: [
    //           {
    //             mealSource: RECOMMENDER_MEAL_SOURCE_SMORG,
    //             id: entry.mealID,
    //             servings: meals[entry.mealID].recipes[0]?.servings || 1,
    //             categoryTags: [],
    //             scaleFactor: 1.0,
    //           },
    //         ],
    //       })),
    //   };
    // });

    console.log('Existing days computed');

    const targetCaloriesPerDay = currentProgrammeTargetCalories(
      currentSpaceMembership,
    );

    const onboardingAnswers = currentSpaceMembership?.onboardingAnswers;

    const programmeHasValidMealTypesAndCalorieSplits =
      (sharedProgramme.mealTypes || []).length > 0 &&
      calorieSplitsAreValid(
        sharedProgramme.mealTypes,
        sharedProgramme.calorieSplits,
      );

    const mealTypes = programmeHasValidMealTypesAndCalorieSplits
      ? sharedProgramme.mealTypes
      : DEFAULT_PROGRAMME_MEAL_TYPES;

    const calorieSplits = programmeHasValidMealTypesAndCalorieSplits
      ? sharedProgramme.calorieSplits
      : DEFAULT_PROGRAMME_CALORIE_SPLITS;

    try {
      let recommendedMenu;
      const { data: healthProMealsByParents } = await lazyGetFullMealsByParents(
        sharedProgrammeAssociatedRecipesBoardIDs,
      );
      const healthProMeals = flattenObjects(
        Object.values(healthProMealsByParents || {}),
      );
      if (!sharedProgramme.copyMealsExactly) {
        const existingDaysMealsOnly = programmeDays.map(
          (day, indexZeroBased) => ({
            dayIndex: indexZeroBased + 1,
            entries: buildExistingMealsFromProgrammePrescribedMeals(
              day.entries,
              healthProMeals,
            ),
          }),
        );

        const recommenderParentIDs = [
          ...ownedRecipesBoardIDs,
          ...sharedProgrammeAssociatedRecipesBoardIDs,
        ];

        recommendedMenu = await recommendMealsOperation(
          mealTypes,
          numDaysToPlan,
          existingDaysMealsOnly,
          1,
          sharedProgramme.nutritionConstraints,
          recommenderPersonalisationDataFromOnboardingAnswers(
            onboardingAnswers,
          ),
          recommenderParentIDs,
          calorieSplits,
          numServings,
          personalisedMealScaling,
          [RecommendationSource.SMORG],
          targetCaloriesPerDay,
          locales,
        );
      } else {
        console.log('Copying program exactly as defined');

        const existingDaysFullContent = programmeDays.map(
          (day, indexZeroBased) => ({
            dayIndex: indexZeroBased + 1,
            entries: buildExistingDaysFromProgrammePrescribedDays(
              day.entries,
              healthProMeals,
              sharedContentEntries,
            ),
          }),
        );

        recommendedMenu = { days: existingDaysFullContent };
      }

      console.log(JSON.stringify(recommendedMenu));

      if (recommendedMenu.errorMessage) {
        console.warn(recommendedMenu.errorMessage);
        dispatch(
          showGlobalSnackbar({
            notificationText: (
              <p>
                Something went wrong, please try again
                <br />
                <br />
                {recommendedMenu.errorMessage}
              </p>
            ),
          }),
        );
        return;
      }

      const flattenedRecommendedEntries = recommendedMenu.days.flatMap(
        (recommendedDay, recommendedDayIndex) => {
          const plannerDbWeekStartDate =
            dbWeeksAndIndexes[recommendedDayIndex].dbWeekStartDate;
          if (!plannerDbWeekStartDate) {
            console.warn(
              `Could not find plannerDbWeekStartDate for recommended day ${recommendedDayIndex}`,
            );
            return null;
          }
          return recommendedDay.entries.map((recommendedEntry) => ({
            recommendedDayIndex,
            recommendedEntry,
            toDbPlannerWeekId:
              dbPlannerWeeksByStartDate[plannerDbWeekStartDate].id,
            toDayIndex: dbWeeksAndIndexes[recommendedDayIndex].dayIndex,
          }));
        },
      );

      const flattenedRecommendedMeals = recommendedMenu.days.flatMap(
        (recommendedDay, recommendedDayIndex) => {
          const plannerDbWeekStartDate =
            dbWeeksAndIndexes[recommendedDayIndex].dbWeekStartDate;
          if (!plannerDbWeekStartDate) {
            console.warn(
              `Could not find plannerDbWeekStartDate for recommended day ${recommendedDayIndex}`,
            );
            return null;
          }
          return recommendedDay.entries
            .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
            .flatMap((recommendedEntry) => recommendedEntry.meals)
            .map((recommendedMeal) => ({
              recommendedDayIndex,
              recommendedMeal,
              toDbPlannerWeekId:
                dbPlannerWeeksByStartDate[plannerDbWeekStartDate].id,
              toDayIndex: dbWeeksAndIndexes[recommendedDayIndex].dayIndex,
            }));
        },
      );

      console.log(JSON.stringify(flattenedRecommendedEntries));

      const importedMeals = await Promise.all(
        flattenedRecommendedMeals.map(
          async ({ recommendedMeal, toDbPlannerWeekId }) => {
            // if (recommendedMeal.mealSource === RECOMMENDER_MEAL_SOURCE_GRC) {
            //   return importGRCRecipeAsMeal(
            //     recommendedMeal.id,
            //     currentSpaceMembership?.id,
            //     toDbPlannerWeekId,
            //     currentHealthProGroup,
            //     recommendedMeal.servings,
            //     recommendedMeal.scaledIngredientsFullText,
            //     recommendedMeal.scaledNutrition,
            //     recommendedMeal.scaledStructuredIngredients,
            //     recommendedMeal.scaledDerivedIngredientNutrition,
            //   );
            // }
            const { data: mealOrSharedMealObj } = await lazyGetMeal(
              recommendedMeal.id,
            );
            if (!mealOrSharedMealObj) {
              console.warn(
                `Could not find meal with ID ${recommendedMeal.id} in meals or in shared meals`,
              );
              return null;
            }
            if (
              recommendedMeal.scaledIngredientsFullText &&
              recommendedMeal.scaledNutrition &&
              recommendedMeal.scaledStructuredIngredients &&
              recommendedMeal.scaledDerivedIngredientNutrition
            ) {
              console.log(`Adding meal to planner with scaling`);
              const input = mealWithScaledIngredients(
                cloneObject(mealOrSharedMealObj),
                recommendedMeal.servings,
                recommendedMeal.scaledIngredientsFullText,
                recommendedMeal.scaledNutrition,
                recommendedMeal.scaledStructuredIngredients,
                recommendedMeal.scaledDerivedIngredientNutrition,
              );
              input.smorgBoardID = toDbPlannerWeekId;
              input.spaceMembershipID = currentSpaceMembership?.id;
              input.groups = currentHealthProGroup && [currentHealthProGroup];
              const { data: insertedMeal } = await createMeal(input);
              return insertedMeal;
            }
            console.log(`Adding meal to planner`);
            const input = cloneObject(mealOrSharedMealObj);
            input.smorgBoardID = toDbPlannerWeekId;
            input.spaceMembershipID = currentSpaceMembership?.id;
            input.groups = currentHealthProGroup && [currentHealthProGroup];
            const { data: insertedMeal } = await createMeal(input);
            return insertedMeal;
          },
        ),
      );

      const flattenedRecommendedContentEntries = recommendedMenu.days.flatMap(
        (recommendedDay, recommendedDayIndex) => {
          const plannerDbWeekStartDate =
            dbWeeksAndIndexes[recommendedDayIndex].dbWeekStartDate;
          if (!plannerDbWeekStartDate) {
            console.warn(
              `Could not find plannerDbWeekStartDate for recommended day ${recommendedDayIndex}`,
            );
            return null;
          }
          return recommendedDay.entries
            .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_CONTENT_ENTRY)
            .map((e) => ({
              recommendedDayIndex,
              objectID: e.objectID,
              toDbPlannerWeekId:
                dbPlannerWeeksByStartDate[plannerDbWeekStartDate].id,
              toDayIndex: dbWeeksAndIndexes[recommendedDayIndex].dayIndex,
            }));
        },
      );

      const importedContentEntries = await Promise.all(
        flattenedRecommendedContentEntries.map(
          async ({ objectID, toDbPlannerWeekId }) => {
            const sharedContentEntryObj = sharedContentEntries[objectID];
            if (!sharedContentEntryObj) {
              console.warn(
                `Could not find shared content entry with ID ${objectID}`,
              );
              return null;
            }
            console.log(`Adding content entry`);
            const clonedContentEntry = cloneObject(sharedContentEntryObj);
            const input = { ...clonedContentEntry };
            input.parentID = toDbPlannerWeekId;
            input.groups = currentHealthProGroup
              ? [currentHealthProGroup]
              : null;
            const { data: createdContentEntry } = await createContentEntry(
              input,
            );
            return createdContentEntry;
          },
        ),
      );

      const recommendedMenuWithPlannerEntries = {
        ...recommendedMenu,
        days: recommendedMenu.days.map(
          (recommendedDay, recommendedDayIndex) => {
            const dbwi = dbWeeksAndIndexes[recommendedDayIndex];
            if (!dbwi) {
              console.warn(
                `Could not find plannerDbWeekStartDate for recommended day ${recommendedDayIndex}`,
              );
              return null;
            }
            const { dayIndex } = dbWeeksAndIndexes[recommendedDayIndex];
            const newEntries = recommendedDay.entries
              .flatMap((entry) => {
                if (entry.entryType === RECOMMENDER_ENTRY_TYPE_MEAL) {
                  return entry.meals.map((recommendedMeal) => {
                    const importedMealsIndex =
                      flattenedRecommendedMeals.findIndex(
                        (frm) =>
                          frm.recommendedDayIndex === recommendedDayIndex &&
                          frm.recommendedMeal?.id === recommendedMeal.id,
                      );
                    // Here we rely on the assumption that the same meal is not recommended twice in a day
                    return {
                      plannerEntryType: PlannerEntryType.MEAL,
                      mealID: importedMeals[importedMealsIndex].id,
                    };
                  });
                }

                if (entry.entryType === RECOMMENDER_ENTRY_TYPE_CONTENT_ENTRY) {
                  const importedContentEntriesIndex =
                    flattenedRecommendedContentEntries.findIndex(
                      (frm) =>
                        frm.recommendedDayIndex === recommendedDayIndex &&
                        frm.objectID === entry.objectID,
                    );
                  return [
                    {
                      plannerEntryType: PlannerEntryType.CONTENT_ENTRY,
                      objectID:
                        importedContentEntries[importedContentEntriesIndex].id,
                    },
                  ];
                }

                return [];
              })
              .filter((e) => e);

            return {
              ...recommendedDay,
              plannerDbWeekStartDate: dbwi.dbWeekStartDate,
              dayIndex,
              entries: newEntries,
            };
          },
        ),
      };
      const updatedPlannerWeeks = { ...dbPlannerWeeksByStartDate };
      /*
Structure of recommendedMenuWithPlannerEntries: 
{
  days: {
    plannerDbWeekStartDate,
    dayIndex, // The day index in the DB week!
    entries: [ // the NEW entries to be added

    ]
  }
}
      */

      recommendedMenuWithPlannerEntries.days.forEach(
        (recommendedDayWithPlannerEntries) => {
          let updatedPlannerWeek =
            updatedPlannerWeeks[
              recommendedDayWithPlannerEntries.plannerDbWeekStartDate
            ];
          recommendedDayWithPlannerEntries.entries.forEach((entry) => {
            if (entry.plannerEntryType === PlannerEntryType.MEAL) {
              updatedPlannerWeek = plannerWeekWithMealAdded(
                updatedPlannerWeek,
                { id: entry.mealID },
                recommendedDayWithPlannerEntries.dayIndex,
              );
            } else if (
              entry.plannerEntryType === PlannerEntryType.CONTENT_ENTRY
            ) {
              updatedPlannerWeek = plannerWeekWithContentEntryAdded(
                updatedPlannerWeek,
                entry.objectID,
                recommendedDayWithPlannerEntries.dayIndex,
              );
            }
          });
          updatedPlannerWeeks[
            recommendedDayWithPlannerEntries.plannerDbWeekStartDate
          ] = updatedPlannerWeek;
        },
      );
      await Promise.all(
        Object.values(updatedPlannerWeeks).map((updatedPlannerWeek) =>
          updateDbPlannerWeek(updatedPlannerWeek),
        ),
      );
      if (sharedProgramme.copyMealsExactly) {
        // Scale to the required number of servings. This set of meals
        // has not been through the recommender so they are unscaled.
        importedMeals.forEach((meal) => {
          const scaleToServingsNumber = Number(numServings) || 1;
          if (scaleToServingsNumber !== meal.recipes[0]?.servings) {
            const scaleFactor =
              scaleToServingsNumber / (meal.recipes[0]?.servings || 1);
            scaleMeal(
              meal,
              scaleFactor,
              scaleToServingsNumber,
              customerFoodDataID,
              false,
              updateMeal,
              dispatch,
              lazyGetMeal,
            );
          }
        });
      } else {
        // Some meals have scaled ingredient strings, ensure they are tokenized
        importedMeals.map(async (m) => {
          const { ingsFullText, analyzedIngredients } =
            await analyzeMissingIngredientsOperation(
              m.recipes[0].ingredients,
              customerFoodDataID,
            );
          if (analyzedIngredients.length > 0) {
            for (let i = 0; i < ingsFullText.length; i += 1) {
              if (analyzedIngredients[i]) {
                analyzedIngredients[i].fullText = ingsFullText[i];
              }
            }
            const updatedMeal = mealWithIngredientTokensUpdated(
              m,
              analyzedIngredients,
            );
            await updateMeal(updatedMeal);
          }
        });
      }
    } catch (e) {
      console.warn(e);
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Something went wrong, please try again',
        }),
      );
    }
  }, [
    affectedDbWeekStartDates,
    createContentEntry,
    createMeal,
    currentSpaceMembership,
    customerFoodDataID,
    dbWeeksAndIndexes,
    dispatch,
    lazyGetFullMealsByParents,
    lazyGetMeal,
    lazyGetOrCreateDbPlannerWeekByStartDate,
    locales,
    numDaysToPlan,
    ownedRecipesBoardIDs,
    plannerViewWeekStartDate,
    programmeStartDay,
    servingsAdults,
    servingsChildren,
    sharedContentEntries,
    sharedProgramme,
    sharedProgrammeAssociatedRecipesBoardIDs,
    sharedProgrammeID,
    startDayIndex,
    updateDbPlannerWeek,
    updateMeal,
  ]);

  const onDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const onRecommendMeals = useCallback(async () => {
    console.log('onRecommendMeals');
    try {
      await callRecommender();
      setRecommendMealsError(false);
      onDismiss();
      navigateToPlanner();
    } catch (e) {
      console.warn(e);
      setRecommendMealsError(true);
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      trackEvents([
        {
          name: 'Recommender called',
          args: {
            num_days_to_plan: numDaysToPlan,
            start_day_index: startDayIndex,
          },
        },
      ]);
    }
  }, [
    callRecommender,
    dispatch,
    navigateToPlanner,
    numDaysToPlan,
    onDismiss,
    startDayIndex,
  ]);

  const onNext = useCallback(async () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setIsRecommenderRunning(true);
      try {
        await onRecommendMeals();
      } finally {
        setIsRecommenderRunning(false);
      }
    }
  }, [currentStepIndex, onRecommendMeals, steps.length]);

  const onBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      navigate(-1);
    }
  }, [currentStepIndex, navigate]);

  // const calendarStartDate =
  //   plannerViewWeekStartDate &&
  //   calendarDateForViewWeekAndIndex(plannerViewWeekStartDate, startDayIndex);

  // const onChangeCalendarStartDate = (calStartDate) => {
  //   const viewWeekStartDate = viewWeekStartDateStr(
  //     calStartDate,
  //     plannerViewWeekStartDay,
  //   );
  //   const dayIndex = dayIndexForCalendarDate(calStartDate);
  //   setPlannerViewWeekStartDate(viewWeekStartDate);
  //   setStartDayIndex(dayIndex);
  // };

  console.log({
    plannerViewWeekStartDate,
    startDayIndex,
    numDaysToPlan,
    servingsAdults,
    servingsChildren,
  });

  if (!sharedProgramme) {
    return null;
  }

  // if (currentStep === STEP_START_DATE) {
  //   return (
  //     <EndUserPlannerRecommenderStepStartDate
  //       calendarStartDate={calendarStartDate}
  //       onChangeCalendarStartDate={onChangeCalendarStartDate}
  //       onNext={onNext}
  //       onBack={onBack}
  //       isLoading={isLoading}
  //       onDismiss={onDismiss}
  //     />
  //   );
  // }
  if (currentStep === STEP_DAYS) {
    return (
      <EndUserPlannerRecommenderStepDays
        canChangeNumDays={!copyMealsExactly}
        numDaysToPlan={numDaysToPlan}
        servingsAdults={servingsAdults}
        servingsChildren={servingsChildren}
        onChangeNumDaysToPlan={setNumDaysToPlan}
        onChangeServingsAdults={setServingsAdults}
        onChangeServingsChildren={setServingsChildren}
        onNext={onNext}
        onBack={onBack}
        isLoading={anyResourceLoading}
        isRecommenderRunning={isRecommenderRunning}
        onDismiss={onDismiss}
        recommendMealsError={recommendMealsError}
      />
    );
  }
  return null;
};

export default EndUserPlannerRecommenderWizard;
